/**
 * NovaChat design tokens — the single source for web (Tailwind v4 theme, see css.ts)
 * and mobile (import these objects directly).
 *
 * Direction "D", approved 2026-11-01: layout from mockup A, graphite colors from B,
 * motion from C. Brand accents come from Novadan's tailwind.config.ts.
 *
 * Contrast rules (enforced in test/tokens.test.ts):
 * - fg.1, fg.body and fg.2 are readable on every surface.
 * - fg.3 is for timestamps and hints on surface 0–2 only; do not put it on surface 3.
 */
export const color = {
    /** Grounds, darkest first: window/sidebar, content, cards/inputs, selected/hover. */
    surface: { 0: '#08090C', 1: '#0F1217', 2: '#151920', 3: '#1D222B' },
    line: { DEFAULT: '#20252E', strong: '#2C333E' },
    /** Text: headings, message body, secondary, timestamps/hints. */
    fg: { 1: '#ECEFF3', body: '#D4D9E0', 2: '#A9B1BD', 3: '#7D8694' },
    /** Novadan orange: B500 primary buttons and badges, B400 open mic and live dot, B300 mentions. */
    brand: { 500: '#CC4D1E', 400: '#ED8B5D', 300: '#F3A37B' },
    /** Novadan N400 / N200. */
    indigo: { 400: '#595ABA', 200: '#A2A3D8' },
    /** Novadan T400: links, read receipts. */
    teal: { 400: '#6ACBDC' },
    /** 500–800: the call bar's moving gradient, under white text. */
    green: { 400: '#6BC676', 500: '#3DA65F', 700: '#23864A', 800: '#17633A' },
    // 600–800 and red 600/800: the call bar's flow when the connection is fair (yellow) or poor (red).
    yellow: { 500: '#FCDF3C', 600: '#C08F0A', 700: '#9C7406', 800: '#765803' },
    red: { 400: '#D14343', 600: '#B33333', 800: '#862525' },
    /** Middle and end stops of Novadan's border-gradient. */
    pink: { 500: '#FF008A' },
    sky: { 200: '#A4E2EB' },
    status: { online: '#6BC676', idle: '#FCDF3C', dnd: '#D14343', offline: '#59616E' },
    white: '#FFFFFF',
    black: '#000000',
};
/** Novadan border-gradient. Speaking ring and connected-room border; rotates (motion.duration.ring / border). */
export const gradient = {
    live: [color.indigo[400], color.pink[500], color.sky[200]],
};
/** Avatar backgrounds with their initials color. Assign per user deterministically. */
export const avatar = [
    { bg: '#595ABA', fg: '#FFFFFF' },
    { bg: '#ED8B5D', fg: '#1A0E08' },
    { bg: '#6ACBDC', fg: '#08262B' },
    { bg: '#6BC676', fg: '#0B2410' },
    { bg: '#FCDF3C', fg: '#3A3000' },
];
/** Corner radius in px: badge, list item, button/card/input, voice controls/composer, panels/tiles. */
export const radius = { xs: 6, sm: 8, md: 12, lg: 16, xl: 20 };
export const font = {
    heading: { family: 'Quicksand', fallback: ['system-ui', 'sans-serif'], weights: [500, 600, 700] },
    body: { family: 'Figtree', fallback: ['system-ui', 'sans-serif'], weights: [400, 500, 600, 700] },
    mono: { family: 'JetBrains Mono', fallback: ['ui-monospace', 'monospace'], weights: [400, 500] },
};
export const motion = {
    /** Milliseconds. */
    duration: {
        hover: 180,
        enter: 550,
        shake: 500,
        typing: 1200,
        blink: 1400,
        glow: 1800,
        chip: 2000,
        ring: 2400,
        border: 6000,
    },
    easing: {
        /** Soft deceleration for things that arrive (new messages, hover lift). */
        out: [0.2, 0.8, 0.2, 1],
        easeOut: [0, 0, 0.58, 1],
        inOut: [0.42, 0, 0.58, 1],
        linear: [0, 0, 1, 1],
        shake: [0.36, 0.07, 0.19, 0.97],
    },
    hoverLiftPx: 1,
    pressScale: 0.96,
    enterOffsetPx: 10,
    /** Outer reach of the speaking glow, px (small avatar / large tile avatar). */
    glowSpreadPx: { sm: 9, lg: 28 },
};
export const tokens = { color, gradient, avatar, radius, font, motion };
//# sourceMappingURL=tokens.js.map